import React from "react";
import c from "classnames";
import { button } from "components/designTokens";
import { Pen } from "components/icons";

export function List({
  categoryList,
  showCreationForm,
  onCreateClick,
  onCancelCreateClick,
  onCreateSubmit,
  onEditSubmit,
}) {
  return (
    <div className="mt-8">
      <Header
        onCreateClick={onCreateClick}
        showCreateButton={!showCreationForm}
      ></Header>
      {showCreationForm ? (
        <CreateCategoryForm
          onCancleClick={onCancelCreateClick}
          onSubmit={onCreateSubmit}
        ></CreateCategoryForm>
      ) : null}
      {categoryList.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md mt-8">
          <ul>
            {categoryList.map((item, index) => {
              return (
                <CategoryItem
                  key={item.id}
                  category={item}
                  bordered={index > 0}
                  onEditSubmit={onEditSubmit}
                ></CategoryItem>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function Header({ onCreateClick, showCreateButton }) {
  return (
    <div className="pb-5 border-b border-gray-200 space-y-3 sm:flex sm:items-center sm:justify-between sm:space-x-4 sm:space-y-0">
      <h3 className="text-lg leading-6 font-medium text-gray-900">Category</h3>
      <div>
        {showCreateButton ? (
          <span className="shadow-sm rounded-md">
            <button
              className={`inline-flex items-center px-4 py-2 text-sm leading-5 font-medium ${button.primary}`}
              onClick={onCreateClick}
            >
              Create new category
            </button>
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function CategoryItem({ category, bordered, onEditSubmit }) {
  const [isEditing, setIsEditing] = React.useState(false);
  return (
    <li
      key={category.id}
      className={c({ "border-t border-gray-200": bordered })}
    >
      <div className="px-4 py-4 flex items-center sm:px-6">
        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <div className="text-sm leading-5 font-medium text-indigo-600 truncate">
              {category.title}
              <span className="ml-1 font-normal text-gray-500">
                {`(${category.slug})`}
              </span>
            </div>
            <div className="mt-2 flex">
              <div className="flex items-center text-sm leading-5 text-gray-500">
                <svg
                  className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Created on {category.created_at}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-5 flex-shrink-0">
          <button
            className="bg-orange-500 text-white rounded-full p-1 hover:bg-orange-400 cursor-pointer focus:outline-none active:outline-none"
            onClick={() => setIsEditing(true)}
          >
            <Pen></Pen>
          </button>
        </div>
      </div>
      {isEditing ? (
        <EditCategoryForm
          category={category}
          onCancleClick={() => {
            setIsEditing(false);
          }}
          onSubmit={async (category) => {
            await onEditSubmit(category);
            setIsEditing(false);
          }}
        ></EditCategoryForm>
      ) : null}
    </li>
  );
}

export function CreateCategoryForm({ onCancleClick, onSubmit }) {
  const handleSubmit = (event) => {
    event.preventDefault();
    const { categoryName, slug } = event.target.elements;
    onSubmit({ name: categoryName.value, slug: slug.value });
  };
  const handleCancel = (event) => {
    event.preventDefault();
    onCancleClick();
  };
  return (
    <form className="mt-8" onSubmit={handleSubmit}>
      <div className="shadow sm:rounded-md sm:overflow-hidden">
        <div className="px-4 py-5 bg-white sm:p-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-3 sm:col-span-2">
              <label
                htmlFor="categoryName"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Category Name
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  id="categoryName"
                  name="categoryName"
                  required
                  className="form-input flex-1 block w-full  rounded-md transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>
            </div>
            <div className="col-span-3 sm:col-span-2">
              <label
                htmlFor="slug"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Slug
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  id="slug"
                  name="slug"
                  required
                  className="form-input flex-1 block w-full  rounded-md transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <span className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              className={`py-2 px-4 bg-white border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out`}
              onClick={handleCancel}
            >
              Cancel
            </button>
          </span>
          <span className="ml-2 inline-flex rounded-md shadow-sm">
            <button
              type="submit"
              className={`inline-flex items-center px-4 py-2 text-sm leading-5 font-medium ${button.primary}`}
            >
              Create
            </button>
          </span>
        </div>
      </div>
    </form>
  );
}

export function EditCategoryForm({ category, onCancleClick, onSubmit }) {
  const handleSubmit = (event) => {
    event.preventDefault();
    const { categoryName, slug } = event.target.elements;
    onSubmit({
      ...category,
      title: categoryName.value,
      slug: slug.value,
    });
  };
  const handleCancel = (event) => {
    event.preventDefault();
    onCancleClick();
  };
  return (
    <form className="mt-4" onSubmit={handleSubmit}>
      <div className="sm:overflow-hidden">
        <div className="px-4 py-5 bg-white sm:p-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-3 sm:col-span-2">
              <label
                htmlFor="categoryName"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Category Name
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  id="categoryName"
                  name="categoryName"
                  defaultValue={category.title}
                  required
                  className="form-input flex-1 block w-full  rounded-md transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>
            </div>
            <div className="col-span-3 sm:col-span-2">
              <label
                htmlFor="slug"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Slug
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  id="slug"
                  name="slug"
                  defaultValue={category.slug}
                  required
                  className="form-input flex-1 block w-full  rounded-md transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <span className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              className={`py-2 px-4 bg-white border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out`}
              onClick={handleCancel}
            >
              Cancel
            </button>
          </span>
          <span className="ml-2 inline-flex rounded-md shadow-sm">
            <button
              type="submit"
              className={`inline-flex items-center px-4 py-2 text-sm leading-5 font-medium ${button.primary}`}
            >
              Save
            </button>
          </span>
        </div>
      </div>
    </form>
  );
}
